import {
  Identifiers,
  SessionState,
  UserSessionState,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/context';
import { ERROR_CODES, isV2Error, V2Error } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/error';
import * as events from '@jetfabric/schemas/lib/protocol-sdk-api-v2/events';
import {
  EventProductView,
  V2_EVENT_TYPES,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/events';
import * as models from '@jetfabric/schemas/lib/protocol-sdk-api-v2/models';
import { V2Transport } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/protocol';
import { Literal } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/data-reference';
import * as queries from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/queries';
import {
  DEFAULT_TRACE_OPTIONS,
  TraceEventType,
  TraceOptions,
  TraceResult,
  TraceState,
  TraceTimestamp,
} from '@jetfabric/schemas/lib/protocol-sdk-api-v2/query/trace';
import { V2Request } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/request';
import { ProductListResult } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/results';
import { inject, injectable, optional } from 'inversify';
// required for event store
import { EVENT_TYPES, ProductViewEvent } from '../client-events';
import { DI_CONSTANTS, DI_TYPES } from '../di-types';
import { JetfabricError, JFSDKError } from '../errors';
import { BoundedEventStore } from '../event-store';
import { ClientEventDBProvider } from '../util/client-event-db-provider';
import {
  getTimezone as _getTimezone,
  perfTimestampFactory as _perfTimestampFactory,
  timestampFactory as _timestampFactory,
  withTimeout,
} from '../util/time';
import { getWindowObject as _getWindowObject } from '../util/window-provider';
import { ClientEventDB } from './client-event-db/client-event-db';
import { FeatureName, V2ClientFeatureState } from './client-state/features';
import { V2ClientStateFactory } from './client-state/root';
import { ClientStateRoot } from './client-state/schema';
import { V2ClientSideRequestProcessor } from './v2request-processor';
import { getCurrentCanonicalUrl, getCurrentUrl } from '../util/context';

interface ValueWithTraceResult<T> {
  value: T;
  traceResult?: TraceResult;
}
type Resolve = (value: ValueWithTraceResult<any>) => void;
type Reject = (reason: ValueWithTraceResult<V2Error | undefined>) => void;

export type TraceCallbackFn = (trace: TraceResult) => void;
export type TraceCallback = TraceCallbackFn | undefined | Promise<TraceCallbackFn | undefined>;

interface ResolvableQuery {
  index: number;
  query: queries.V2Query;
  promise: Promise<any>;
  resolve: Resolve;
  reject: Reject;
  traceCallback: TraceCallback;
  traceOptions?: Partial<TraceOptions>;
}

interface InternalRequest {
  queries: ResolvableQuery[];
  events: events.V2Event[];
}

const traceStateToResult = (traceState?: TraceState): TraceResult | undefined =>
  traceState
    ? {
        options: traceState.options,
        context: traceState.context,
      }
    : undefined;

@injectable()
export class V2ClientSDK {
  private transport: V2Transport;
  private timestampFactory: typeof _timestampFactory;
  private perfTimestampFactory: typeof _perfTimestampFactory;
  private getTimezone: typeof _getTimezone;
  private eventStore: BoundedEventStore;
  private requestProcessor: V2ClientSideRequestProcessor;
  private defaultTimeout: number;
  private getWindowObject: typeof _getWindowObject;
  private clientStateFactory: V2ClientStateFactory;
  private clientState?: ClientStateRoot;
  private clientFeatureState: V2ClientFeatureState;
  private clientEventDB?: ClientEventDB;
  private clientEventDBProvider?: ClientEventDBProvider;
  _defaultTraceCallback: TraceCallback;

  private identifiers: Partial<Identifiers> = {
    addresses: [],
  };
  private userSessionState: UserSessionState = {};
  private nextRequest: InternalRequest = {
    events: [],
    queries: [],
  };

  private traceResultToState(traceResult?: TraceResult): TraceState | undefined {
    return traceResult
      ? { ...traceResult, getTraceTimestamp: this.getTraceTimestamp.bind(this) }
      : undefined;
  }

  private async saveEvents(events: events.V2Event[]) {
    // TODO: remove (deprected)
    // Add events to localStorage-backed eventStore.
    // eventStore uses V1 event types, so v2 to v1 conversion is necessary
    const convertedEvents: ProductViewEvent[] = events
      .filter(ev => ev.type === V2_EVENT_TYPES.PRODUCT_VIEW)
      .map(ev => ({
        type: EVENT_TYPES.PRODUCT_VIEW,
        attributes: {
          product: {
            productId: (ev as EventProductView).productId,
            variantId: (ev as EventProductView).variantId,
          },
        },
        // Note: events no longer have an individual timestamp, but
        // the legacy eventStore requires a timestamp, so we generate one
        timestamp: this.getTimestamp(),
      }));
    this.eventStore.add(...convertedEvents);

    // Initialize clientState if necessary
    if (!this.clientState) {
      this.clientState = this.clientStateFactory.getRoot();
    }

    // clientEventDB only available in non-production environments for now
    if (
      this.clientEventDBProvider !== undefined &&
      this.clientFeatureState.getFeatureState('INDEXEDDB_EVENTS') === true
    ) {
      if (this.clientEventDB === undefined) {
        this.clientEventDB = await this.clientEventDBProvider();
      }

      await Promise.all(events.map(async event => await this.clientEventDB?.add(event)));
    }
  }

  private getTimestamp() {
    return +this.timestampFactory();
  }

  private getTraceTimestamp(): TraceTimestamp {
    return {
      abs: +this.timestampFactory(),
      rel: this.perfTimestampFactory(),
    };
  }

  private reset() {
    // identification is not reset because consumer, store do not change within a session.
    this.nextRequest.events = [];
    this.nextRequest.queries = [];
  }

  private queryResultPromise(
    query: queries.V2Query,
    traceCallback?: TraceCallback,
    traceOptions?: Partial<TraceOptions>
  ) {
    let resolvableQuery: Partial<ResolvableQuery> = {
      index: this.nextRequest.queries.length,
      query,
      traceCallback: traceCallback || this._defaultTraceCallback,
      traceOptions,
    };
    resolvableQuery.promise = new Promise((_resolve, _reject) => {
      const maybeInvokeTraceCallback = (traceResult?: TraceResult) => {
        if (resolvableQuery.traceCallback && traceResult) {
          traceResult.context.exitTimestamp = this.getTraceTimestamp();
          // traceCallback can ba async, we don't care about the result
          // this means async callbacks are started, but we don't await
          Promise.resolve(resolvableQuery.traceCallback).then(
            traceCallback => {
              try {
                traceCallback?.(traceResult);
              } catch (e) {
                console.debug(e);
              }
            },
            err => {
              // if traceCallback couldn't be resolved, log exception
              console.debug(err);
            }
          );
        }
      };
      resolvableQuery.resolve = ({ value, traceResult }: ValueWithTraceResult<any>) => {
        maybeInvokeTraceCallback(traceResult);
        _resolve(value);
      };
      resolvableQuery.reject = ({
        value,
        traceResult,
      }: ValueWithTraceResult<V2Error | undefined>) => {
        maybeInvokeTraceCallback(traceResult);
        _reject(value);
      };
    });
    this.nextRequest.queries!.push(resolvableQuery as ResolvableQuery);
    return resolvableQuery.promise;
  }

  private logPerformance(
    source: string,
    currentRequest: InternalRequest,
    data: events.EventLogEntry['data'],
    beforeProcessing: number,
    afterProcessing?: number
  ) {
    if (typeof afterProcessing === 'number') {
      currentRequest.events.push({
        type: V2_EVENT_TYPES.LOG_ENTRY,
        messageType: `SDK:Perf:${source}`,
        data: Object.assign({ duration: afterProcessing - beforeProcessing, ...data }),
      });
    }
  }

  private getSessionState(): SessionState {
    return {
      clientTimestamp: +this.timestampFactory(),
      clientTimezone: this.getTimezone(),
      url: getCurrentUrl(this.getWindowObject()),
      canonicalUrl: getCurrentCanonicalUrl(this.getWindowObject()),
    };
  }

  private rejectPendingPromises(queries: ResolvableQuery[], e: Error) {
    queries.forEach(q => q.reject({ value: e }));
  }

  private async getResponse(currentRequest: InternalRequest) {
    const context = Object.assign(
      {},
      this.userSessionState,
      this.identifiers,
      this.getSessionState()
    );

    const apiRequest: V2Request = {
      context,
      events: currentRequest.events,
      queries: {}, // remote queries added as they are processed below:
    };

    for (let currentQuery of currentRequest.queries) {
      let beforeProcessing = this.getWindowObject().performance?.now();
      let traceState = this.createRootTraceState(currentQuery);
      const processingResult = await this.requestProcessor.processQuery(
        this.clientState!, // clientState was initializaed in the invoking go()
        currentQuery.query,
        apiRequest,
        [], // binding stack is empty
        traceState
      );
      // record completion timestmpa
      if ('error' in processingResult) {
        currentQuery.reject({ value: processingResult.error, traceResult: traceState });
      } else if ('result' in processingResult) {
        currentQuery.resolve({
          value: processingResult.result!,
          traceResult: traceStateToResult(traceState),
        });
      } else if ('partialResult' in processingResult) {
        // copy any partial results to outgoin API request
        apiRequest.queries[currentQuery.index] = { ...processingResult.partialResult };
        if (traceState) {
          // copy trace results for request if included, but omit getTraceTimestamp
          apiRequest.queries[currentQuery.index].traceResult = {
            options: traceState.options,
            context: traceState.context,
          };
        }
      }
    }

    // make API request if there is any data to send.
    if (this.requestNotEmpty(apiRequest)) {
      try {
        const originalServerResponse = await withTimeout(
          this.transport.submitV2(apiRequest),
          this.defaultTimeout
        );
        const queriesHandled: { [key: string]: boolean } = {};
        // resolve any successfully evaluated queries returned by server
        for (let ixStr in originalServerResponse.results) {
          const ix = parseInt(ixStr, 10);
          queriesHandled[ixStr] = true;
          currentRequest.queries[ix].resolve({
            value: originalServerResponse.results[ixStr],
            traceResult: originalServerResponse.trace?.[ix],
          });
        }
        // reject any queries which resulted in errors.
        for (let ixStr in originalServerResponse.errors) {
          const ix = parseInt(ixStr, 10);
          queriesHandled[ixStr] = true;
          currentRequest.queries[ix].reject({
            value: new JFSDKError(originalServerResponse.errors[ixStr]),
            traceResult: originalServerResponse.trace?.[ix],
          });
        }
        // The API can also return a partialResult, which will then be evaluated by RequestProcessor.
        // The result of this evaluation shouldn't be partial.
        if (originalServerResponse.partialResults) {
          for (let ixStr in originalServerResponse.partialResults) {
            const ix = parseInt(ixStr, 10);
            queriesHandled[ixStr] = true;
            const partiallyEvalutedQuery = originalServerResponse.partialResults[ixStr];
            // If tracing was enabled, there should be a traceResults sent along with the partialResult
            // if there wasn't, we can assume tracing is turned off
            const traceState = this.traceResultToState(originalServerResponse?.trace?.[ixStr]);
            const finalResult = await this.requestProcessor.processQuery(
              this.clientState!,
              partiallyEvalutedQuery,
              apiRequest, // pass in context and events for original api request
              [], // cache bindings are empty
              traceState
            );
            if ('result' in finalResult) {
              currentRequest.queries[ix].resolve({
                value: finalResult.result,
                traceResult: traceStateToResult(traceState),
              });
            } else if ('error' in finalResult) {
              currentRequest.queries[ix].reject({
                value: finalResult.error,
                traceResult: traceStateToResult(traceState),
              });
            } else {
              const ex = new JetfabricError('Server-returned');
              currentRequest.queries[ix].reject({
                value: ex,
                traceResult: traceStateToResult(traceState),
              });
            }
          }
        }
        // throw exception for unanswered queries
        currentRequest.queries.forEach(resolvableQuery => {
          if (queriesHandled[resolvableQuery.index] !== true) {
            resolvableQuery.reject({
              value: new JetfabricError(
                `query #${resolvableQuery.index} has no associated result or error in API response!`
              ),
            });
          }
        });
      } catch (e) {
        // if http request has failed, all remote queries fail with the exception
        this.rejectPendingPromises(
          currentRequest.queries.filter(q => q.index in apiRequest.queries),
          e
        );
        // rethrow the exception so that the promise returned by go() is also reject
        throw e;
      }
    }
  }

  private createRootTraceState({
    traceOptions,
    traceCallback,
  }: {
    traceOptions?: Partial<TraceOptions>;
    traceCallback?: TraceCallback;
  }): TraceState | undefined {
    if (traceCallback || traceOptions) {
      // If callback or options provided, tracing is enabled.
      return {
        options: Object.assign({}, DEFAULT_TRACE_OPTIONS, traceOptions) as TraceOptions,
        context: {
          type: TraceEventType.RootContext,
          timestamp: this.getTraceTimestamp(),
          children: [],
        },
        getTraceTimestamp: this.getTraceTimestamp.bind(this),
      };
    }
  }

  private assertIdsSet(currentRequest: InternalRequest) {
    let error = null;
    if (!this.identifiers.appId) {
      error = new JetfabricError('No appId provided, please call window.JFClientSDK.v2.setAppId()');
    }

    if (
      !error &&
      (this.identifiers.addresses || []).filter(a => a.provider === this.identifiers?.appId)
        ?.length < 1
    ) {
      error = new JetfabricError(
        `No app customer address for provider ${this.identifiers.appId} registered, please call window.JFClientSDK.v2.registerCustomerAddress() with this provider`
      );
    }

    if (error) {
      this.rejectPendingPromises(currentRequest.queries, error);
      throw error;
    }
  }

  private requestNotEmpty(apiRequest: V2Request, queryCount?: number) {
    /** Send API request if one of the following is true:
     * 1. User has any addresses
     * 2. There are events
     * 3. There are queries (some or all of which may prove to be local) */

    return (
      (apiRequest.context.addresses?.length || 0) > 0 ||
      apiRequest.events.length > 0 ||
      (typeof queryCount === 'number' && queryCount > 0) ||
      Object.keys(apiRequest.queries).length > 0
    );
  }

  constructor(
    @inject(DI_TYPES.V2Transport) transport: V2Transport,
    @inject(DI_TYPES.TimestampFactory) timestampFactory: typeof _timestampFactory,
    @inject(DI_TYPES.PerfTimestampFactory) perfTimestampFactory: typeof _perfTimestampFactory,
    @inject(DI_TYPES.getTimezone) getTimezone: typeof _getTimezone,
    @inject(DI_TYPES.BoundedEventStore) eventStore: BoundedEventStore,
    @inject(DI_TYPES.V2ClientSideRequestProcessor) requestProcessor: V2ClientSideRequestProcessor,
    @inject(DI_CONSTANTS.DEFAULT_TIMEOUT) defaultTimeout: number,
    @inject(DI_TYPES.WindowProvider) getWindowObject: typeof _getWindowObject,
    @inject(DI_TYPES.V2ClientStateFactory) clientStateFactory: V2ClientStateFactory,
    @inject(DI_TYPES.V2ClientFeatureState) clientFeatureState: V2ClientFeatureState,
    // clientEventDBProvider is optional, its value can be undefined!
    @inject(DI_TYPES.ClientEventDBProvider)
    @optional()
    clientEventDBProvider?: ClientEventDBProvider
  ) {
    this.transport = transport;
    this.timestampFactory = timestampFactory;
    this.perfTimestampFactory = perfTimestampFactory;
    this.getTimezone = getTimezone;
    this.eventStore = eventStore;
    this.requestProcessor = requestProcessor;
    this.defaultTimeout = defaultTimeout;
    this.getWindowObject = getWindowObject;
    this.clientStateFactory = clientStateFactory;
    this.clientFeatureState = clientFeatureState;
    this.clientEventDBProvider = clientEventDBProvider;
    this.reset();
  }

  addClientStateProperty(propertyName: string, value: any) {
    this.clientStateFactory.addCustomProperty(propertyName, value);
    return this;
  }

  setFeatureState(featureName: FeatureName, isEnabled: boolean) {
    this.clientFeatureState.setFeatureState(featureName, isEnabled);
    return this;
  }

  registerCustomerAddress(address: models.ProviderCustomerAddress) {
    this.identifiers.addresses?.push(address);
    return this;
  }

  setAppId(appId: string) {
    this.identifiers.appId = appId;
    return this;
  }

  setUserSessionSate(properties: Record<string, any>) {
    this.userSessionState = {
      ...this.userSessionState,
      ...properties,
    };
    return this;
  }

  registerProductView(product: models.Product) {
    this.nextRequest.events!.push({
      type: events.V2_EVENT_TYPES.PRODUCT_VIEW,
      ...product,
    });

    return this;
  }

  registerCategoryView(category: models.ProductCategory) {
    this.nextRequest.events!.push({
      type: events.V2_EVENT_TYPES.CATEGORY_VIEW,
      ...category,
    });

    return this;
  }

  registerCustomEvent(type: string, properties: Record<string, any>) {
    this.nextRequest.events!.push({
      type,
      ...properties,
    });

    return this;
  }

  registerPageView() {
    this.nextRequest.events!.push({
      type: events.V2_EVENT_TYPES.PAGE_VIEW,
      title: this.getWindowObject()?.document?.title,
      referrer: this.getWindowObject()?.document?.referrer,
      userAgent: this.getWindowObject()?.navigator?.userAgent,
      url: this.getWindowObject()?.location?.href,
      host: this.getWindowObject()?.location?.host,
      path: this.getWindowObject()?.location?.pathname,
      query: this.getWindowObject()?.location?.search,
      hash: this.getWindowObject()?.location?.hash,
      viewportWidth: this.getWindowObject()?.innerWidth,
      viewportHeight: this.getWindowObject()?.innerHeight,
    });

    return this;
  }

  getPreviouslyViewedProducts(options?: Partial<queries.QueryPreviouslyViewedProducts>) {
    return this.queryResultPromise(
      Object.assign({}, options, {
        type: queries.V2_QUERY_TYPES.PREVIOUSLY_VIEWED_PRODUCTS,
      })
    ) as Promise<ProductListResult>;
  }

  getMostPopularProducts(options?: Partial<queries.QueryMostPopularProducts>) {
    return this.queryResultPromise(
      Object.assign({}, options, {
        type: queries.V2_QUERY_TYPES.MOST_POPULAR_PRODUCTS,
      })
    ) as Promise<ProductListResult>;
  }

  getCustomerHasTag(...tags: string[]) {
    return this.queryResultPromise({
      in: tags,
      type: queries.V2_QUERY_TYPES.CUSTOMER_HAS_TAG,
    }) as Promise<boolean>;
  }

  getGraphQLResult(options: Omit<queries.QueryGraphQL, 'type'>) {
    return this.queryResultPromise({
      type: queries.V2_QUERY_TYPES.GRAPHQL,
      ...options,
    });
  }

  getQueryResult(
    query: queries.V2Query,
    traceCallback?: TraceCallbackFn,
    traceOptions?: Partial<TraceOptions>
  ) {
    return this.queryResultPromise(query, traceCallback, traceOptions);
  }

  evaluateConditionalExpression(
    options: Omit<queries.QueryEvaluateCondition, 'type'>,
    traceCallback?: TraceCallbackFn,
    traceOptions?: Partial<TraceOptions>
  ) {
    return this.queryResultPromise(
      {
        type: queries.V2_QUERY_TYPES.CONDITION_EVALUATION,
        ...options,
      },
      traceCallback,
      traceOptions
    ) as Promise<Literal>;
  }

  evaluateTemplate(options: Omit<queries.QueryEvaluateTemplate, 'type'>) {
    return this.queryResultPromise({
      type: queries.V2_QUERY_TYPES.TEMPLATE_EVALUATION,
      ...options,
    }) as Promise<string>;
  }

  log(properties: Omit<events.EventLogEntry, 'type'>) {
    this.nextRequest.events!.push({
      ...properties,
      type: V2_EVENT_TYPES.LOG_ENTRY,
      messageType: properties.messageType || models.DEFAULT_LOG_MESSAGE_TYPE,
      severity: properties.severity || models.SEVERITY.INFO,
    });
    return this;
  }

  /**
   * go() creates a request object, and -if not empty-
   * processes locally computable queries, sending the remaining
   * portion of the request to the API.
   * Since send a request empties the "current request" state, calling
   * go() multiple times is harmless, only the first go() will attempt to
   * process a request.
   * The promise returned by go() resolves to null if the request was successfully
   * processed (actual query results can be read from the promises returned by the
   * get*() functions which declare these queries). If the HTTP request fails, the
   * promise returned by go() will also fail with a JetfabricError.
   */
  async go(): Promise<null> {
    if (
      this.requestNotEmpty(
        {
          context: this.identifiers,
          events: this.nextRequest.events,
          queries: {},
        },
        this.nextRequest.queries.length
      )
    ) {
      const currentRequest: InternalRequest = Object.assign({}, this.nextRequest);
      // while the currently sent request is in-flight, the client
      // can start building the next request.
      this.reset();
      this.assertIdsSet(currentRequest);

      await this.saveEvents(currentRequest.events || []);

      await this.getResponse(currentRequest);
    }
    return null;
  }
}
